import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  AppBar,
  Toolbar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Check,
  Close,
  Pause,
  Comment,
  AttachFile,
  Send,
  Logout,
  Person,
} from '@mui/icons-material';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getRequestById, updateRequestStatus, RequestStatus, addCommentToRequest } from "../services/dataServiceWrapper";
import { sendStatusUpdateNotification, sendApprovalToAndres, sendAcademicChangeApprovalNotifications, sendCommentNotification, sendTravelApprovalToInfo, sendMarketingApprovalToMarketing, sendLeaveApprovalToPayroll } from '../services/emailService';
import { getFileInfo, downloadFile } from '../services/fileService';
import { notifyNewComment } from '../services/notificationService';

const statusColors = {
  [RequestStatus.PENDING]: { color: '#f57c00', bg: '#fff3e0', icon: <Pause /> },
  [RequestStatus.IN_PROGRESS]: { color: '#0288d1', bg: '#e1f5fe', icon: <Pause /> },
  [RequestStatus.APPROVED]: { color: '#388e3c', bg: '#e8f5e9', icon: <Check /> },
  [RequestStatus.REJECTED]: { color: '#d32f2f', bg: '#ffebee', icon: <Close /> },
  [RequestStatus.COMPLETED]: { color: '#5e35b1', bg: '#ede7f6', icon: <Check /> },
  [RequestStatus.HOLD]: { color: '#ff6f00', bg: '#fff3e0', icon: <Pause /> },
};

const RequestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accounts, instance } = useMsal();
  const account = accounts[0];
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState('');
  const [actionNotes, setActionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [fileDetails, setFileDetails] = useState([]);

  useEffect(() => {
    loadRequest();
    loadFileDetails();
  }, [id]);

  const loadRequest = async () => {
    try {
      const requestData = await getRequestById(id);
      if (requestData) {
        // Initialize notes array if it doesn't exist or is not an array
        if (!requestData.notes || !Array.isArray(requestData.notes)) {
          requestData.notes = [];
        }
        setRequest(requestData);
      } else {
        toast.error('Request not found');
        navigate('/my-requests');
      }
    } catch (error) {
      console.error('Error loading request:', error);
      toast.error('Error loading request');
    } finally {
      setLoading(false);
    }
  };
  
  const loadFileDetails = async () => {
    try {
      const files = await getFileInfo(id);
      setFileDetails(files);
    } catch (error) {
      console.error('Error loading file details:', error);
    }
  };

  const isApprover = () => {
    const userEmail = account?.email || account?.username;

    // Global viewers - can see everything but not necessarily take action
    const globalViewers = [
      'amitchell@acom.edu.au',
      'hhildebrandt@acom.edu.au',
      'rblake@acom.edu.au',    // Ruby Blake - Accounts
      'lgale@acom.edu.au'       // Lynn Gale - Accounts
    ];
    if (globalViewers.some(email => email.toLowerCase() === userEmail?.toLowerCase())) {
      return true;
    }

    // Check single approver (all request types with a single approver)
    if (request?.approver?.toLowerCase() === userEmail?.toLowerCase()) {
      return true;
    }

    // Check multiple approvers (academic changes)
    if (request?.approvers && Array.isArray(request.approvers)) {
      return request.approvers.some(approverEmail =>
        approverEmail.toLowerCase() === userEmail.toLowerCase()
      );
    }

    return false;
  };

  const canTakeAction = () => {
    return isApprover() && (request?.status === RequestStatus.PENDING || request?.status === RequestStatus.HOLD);
  };

  const handleAction = (action) => {
    setSelectedAction(action);
    setActionDialogOpen(true);
  };

  const processAction = async () => {
    setProcessing(true);
    try {
      // Update request status
      const userEmail = account?.email || account?.username || 'current.user@acom.edu.au';
      const updatedRequest = await updateRequestStatus(request.id, selectedAction, userEmail);
      
      if (updatedRequest) {
        // Add approver notes
        if (actionNotes) {
          updatedRequest.approverNotes = actionNotes;
          updatedRequest.notes = updatedRequest.notes || [];
          updatedRequest.notes.push({
            id: Date.now(),
            author: account?.name || account?.email || 'Approver',
            authorEmail: account?.email || account?.username,
            content: `Status changed to ${selectedAction.toUpperCase()}: ${actionNotes}`,
            timestamp: new Date().toISOString(),
            isApprover: true,
          });
        }

        // Send notifications
        try {
          await sendStatusUpdateNotification(updatedRequest, selectedAction, actionNotes);
          
          if (selectedAction === RequestStatus.APPROVED) {
            // For extensions requests, send to Andres AND extensions@acom.edu.au
            if (updatedRequest.type === 'extensions') {
              await sendApprovalToAndres(updatedRequest);

              // Also send to extensions@acom.edu.au
              try {
                await fetch('https://apps.acom.edu.au/api/emails/extension-approved-notification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ request: updatedRequest })
                });
              } catch (err) {
                console.error('Failed to send to extensions@:', err);
              }
            }
            
            // For academic changes, send to secondary recipients
            if (updatedRequest.type === 'academic' && updatedRequest.secondaryRecipients) {
              // Update the request with approval info
              updatedRequest.approvedBy = userEmail;
              updatedRequest.approvalDate = new Date().toISOString();
              
              // Send to all secondary recipients
              await sendAcademicChangeApprovalNotifications(updatedRequest, updatedRequest.secondaryRecipients);
            }
            
            // For travel requests, send to info@
            if (updatedRequest.type === 'travel') {
              // Update the request with approval info
              updatedRequest.approvedBy = userEmail;
              updatedRequest.approvalDate = new Date().toISOString();

              await sendTravelApprovalToInfo(updatedRequest);
            }

            // For marketing requests, send to marketing team
            if (updatedRequest.type === 'marketing') {
              // Update the request with approval info
              updatedRequest.approvedBy = userEmail;
              updatedRequest.approvalDate = new Date().toISOString();

              await sendMarketingApprovalToMarketing(updatedRequest);
            }

            // For leave requests, send to payroll and create calendar event
            if (updatedRequest.type === 'leave') {
              // Update the request with approval info
              updatedRequest.approvedBy = userEmail;
              updatedRequest.approvalDate = new Date().toISOString();

              await sendLeaveApprovalToPayroll(updatedRequest);

              // Create calendar event in Nettie's Leave Calendar (using app-only auth)
              try {
                await fetch('https://apps.acom.edu.au/api/calendar/create-leave-event', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ request: updatedRequest })
                });
                console.log('Calendar event created for leave request');
              } catch (calendarError) {
                console.error('Failed to create calendar event:', calendarError);
                // Don't fail the approval if calendar creation fails
              }
            }

            // For expense reimbursement, send to accounts
            if (updatedRequest.type === 'reimbursement') {
              // Update the request with approval info
              updatedRequest.approvedBy = userEmail;
              updatedRequest.approvalDate = new Date().toISOString();

              try {
                await fetch('https://apps.acom.edu.au/api/emails/expense-approved-notification', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ request: updatedRequest })
                });
              } catch (err) {
                console.error('Failed to send expense approval to accounts@:', err);
              }
            }
          }
        } catch (emailError) {
          console.error('Email notification failed:', emailError);
          // Don't fail the action if email fails
        }

        toast.success(`Request ${selectedAction}`);
        setRequest(updatedRequest);
        setActionDialogOpen(false);
        setActionNotes('');
      }
    } catch (error) {
      console.error('Error processing action:', error);
      toast.error('Failed to process action');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const userEmail = account?.email || account?.username;
      const comment = {
        id: Date.now(),
        author: account?.name || account?.email || 'User',
        authorEmail: userEmail,
        content: newComment,
        timestamp: new Date().toISOString(),
        isApprover: isApprover(),
      };

      // Add comment to request
      const updatedRequest = await addCommentToRequest(request.id, comment);
      
      if (updatedRequest) {
        // Send email notifications
        const recipientsToNotify = new Set();
        
        // Notify the requester (unless they're the comment author)
        if (request.submittedBy !== userEmail) {
          recipientsToNotify.add(request.submittedBy);
        }
        
        // Notify approvers (unless they're the comment author)
        if (request.approver && request.approver !== userEmail) {
          recipientsToNotify.add(request.approver);
        }
        
        // For academic changes with multiple approvers
        if (request.approvers && Array.isArray(request.approvers)) {
          request.approvers.forEach(approver => {
            if (approver !== userEmail) {
              recipientsToNotify.add(approver);
            }
          });
        }
        
        // Send email to each recipient
        recipientsToNotify.forEach(async (recipientEmail) => {
          try {
            await sendCommentNotification(updatedRequest, comment, recipientEmail);
          } catch (emailError) {
            console.error('Failed to send email notification to', recipientEmail, emailError);
          }
        });
        
        // Create bell icon notifications
        notifyNewComment(updatedRequest, comment, userEmail);
        
        setRequest(updatedRequest);
        setNewComment('');
        toast.success('Comment added');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!request) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/my-requests')}
            sx={{ mr: 2 }}
          >
            Back to My Requests
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ff6b00' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <Grid container spacing={3}>
          {/* Request Details */}
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Paper sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {request.title}
                  </Typography>
                  <Chip
                    label={request.status.replace('_', ' ').toUpperCase()}
                    sx={{
                      backgroundColor: statusColors[request.status].bg,
                      color: statusColors[request.status].color,
                      fontWeight: 'bold',
                    }}
                    icon={statusColors[request.status].icon}
                  />
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Debug info - remove in production */}
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, display: 'none' }}>
                  <Typography variant="caption" color="text.secondary">
                    Debug: Request Type = "{request.type}", ID = {request.id}
                  </Typography>
                </Box>

                {request.type === 'extensions' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Student Name</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.studentName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Student Number</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.studentNumber}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Student Email</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.studentEmail}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Award</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.award}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">SEM</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.sem}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Units and Assignments</Typography>
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{request.unitsAndAssignments}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Explanation of Student's Situation</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.studentSituation}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Requested Adjustments</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.requestedAdjustments}</Typography>
                      </Grid>
                      {request.academicDeanResponse && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Associate Academic Dean Response</Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>{request.academicDeanResponse}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Approver</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.approver}</Typography>
                      </Grid>
                    </Grid>
                  </>
                )}

                {request.type === 'academic' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Change Type</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.changeTypeName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Unit Code</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.unitCode}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Reason for Change</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.reason}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Requested Action</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.requestedAction}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Approvers</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {request.approvers ? request.approvers.join(', ') : request.approver}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}

                {request.type === 'travel' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Submitter</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.submitterName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Destination</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.destinationCity}</Typography>
                      </Grid>
                      {request.purpose && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="subtitle2" color="text.secondary">Purpose of Travel</Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>{request.purpose}</Typography>
                        </Grid>
                      )}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Arrival Time</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {new Date(request.arrivalTime).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Departure Time</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {new Date(request.departureTime).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Travellers</Typography>
                        {request.travellers && request.travellers.map((traveller, index) => (
                          <Typography key={index} variant="body1">
                            • {traveller.name} - Departing from: {traveller.departureCity}
                          </Typography>
                        ))}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Accommodation Required</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {request.accommodationRequired ? 'Yes' : 'No'}
                          {request.accommodationRequired && request.accommodationSuburb && 
                            ` (${request.accommodationSuburb})`
                          }
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Car Hire Required</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {request.carHireRequired ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                      {request.additionalRequirements && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Additional Requirements</Typography>
                          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                            {request.additionalRequirements}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Approver</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {request.approverName || request.approver || 'Not assigned'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}

                {request.type === 'leave' && (
                  <>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium', color: 'primary.main' }}>
                        Leave Request Details
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Submitter</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.submitterName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Leave Type</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.leaveType}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">First Day of Leave</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {new Date(request.firstDayOfLeave).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Last Day of Leave</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {new Date(request.lastDayOfLeave).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Days/Hours</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.daysHoursText || `${request.daysRequested} Days`}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Manager</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.approver}</Typography>
                      </Grid>
                    </Grid>
                    
                    {/* Leave Notes Section */}
                    {request.leaveReason && (
                      <>
                        <Divider sx={{ my: 3 }} />
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'medium' }}>
                            Additional Notes
                          </Typography>
                          <Box sx={{ 
                            p: 3, 
                            bgcolor: 'background.paper', 
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: '#f8f9fa'
                          }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>
                              {request.leaveReason}
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}
                  </>
                )}

                {request.type === 'reimbursement' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Submitter</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.submitterName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Cost</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>${request.cost}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Expense Description</Typography>
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{request.expenseDescription}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Account Name</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.accountName || 'On file'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">BSB</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.bsb || 'On file'}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="subtitle2" color="text.secondary">Account Number</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.accountNumber || 'On file'}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Approver</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.approver || 'Finance Department (accounts@acom.edu.au)'}</Typography>
                      </Grid>
                    </Grid>
                  </>
                )}

                {request.type === 'marketing' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Submitter</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.submitterName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Date Required</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                          {new Date(request.dateRequired).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">What are you marketing?</Typography>
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{request.marketingSubject}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">What materials do you need?</Typography>
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{request.materialsNeeded}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Keywords and Tone</Typography>
                        <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>{request.keywordsAndTone}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Budget</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.budget}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Approver</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.approverName || request.approver || 'Not assigned'}</Typography>
                      </Grid>
                    </Grid>
                  </>
                )}

                {request.type === 'wfh' && (
                  <>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Submitter</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.submitterName}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="subtitle2" color="text.secondary">Compliance Status</Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: request.allChecked ? '#4caf50' : '#ff9800', fontWeight: 'bold' }}>
                          {request.allChecked ? 'All Items Compliant' : 'Items Require Remediation'}
                        </Typography>
                      </Grid>
                      {!request.allChecked && request.uncheckedQuestions && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Items Requiring Attention ({request.uncheckedQuestions.length})</Typography>
                          <Box component="ul" sx={{ pl: 3 }}>
                            {request.uncheckedQuestions.map((question, idx) => (
                              <Typography component="li" key={idx} variant="body2" sx={{ mb: 1 }}>
                                {question}
                              </Typography>
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {request.wfhComments && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="text.secondary">Comments</Typography>
                          <Typography variant="body1" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                            {request.wfhComments}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}

                {/* Fallback for any request type not explicitly handled above */}
                {request.type !== 'extensions' && request.type !== 'academic' && request.type !== 'travel' && request.type !== 'leave' && request.type !== 'reimbursement' && request.type !== 'marketing' && request.type !== 'wfh' && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="text.secondary">Request Type</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{request.type}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Submitted By</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>{request.submittedBy}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Submitted Date</Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {new Date(request.submittedDate).toLocaleString()}
                      </Typography>
                    </Grid>
                    {request.approver && (
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" color="text.secondary">Approver</Typography>
                        <Typography variant="body1" sx={{ mb: 2 }}>{request.approver}</Typography>
                      </Grid>
                    )}
                    {/* Display all other fields dynamically */}
                    {Object.entries(request).map(([key, value]) => {
                      // Skip system fields and fields already displayed
                      if (['id', 'type', 'title', 'status', 'submittedBy', 'submittedDate', 'approver', 'notes', 'attachments'].includes(key)) {
                        return null;
                      }
                      // Only display non-null, non-empty values
                      if (value && value !== '') {
                        return (
                          <Grid item xs={12} sm={6} key={key}>
                            <Typography variant="subtitle2" color="text.secondary">
                              {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                            </Typography>
                          </Grid>
                        );
                      }
                      return null;
                    })}
                  </Grid>
                )}

                {/* Supporting Documents */}
                {fileDetails.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2 }}>Supporting Documents</Typography>
                    <List>
                      {fileDetails.map((file) => (
                        <ListItem key={file.id}>
                          <AttachFile sx={{ mr: 2, color: file.exists ? 'primary.main' : 'error.main' }} />
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography>{file.file_name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {(file.file_size / 1024).toFixed(1)} KB • {new Date(file.uploaded_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                          {file.exists ? (
                            <Button
                              size="small"
                              onClick={() => downloadFile(id, file.file_name, file.file_name)}
                            >
                              Download
                            </Button>
                          ) : (
                            <Typography variant="caption" color="error">
                              File not found
                            </Typography>
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {/* Action Buttons for Approvers */}
                {canTakeAction() && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You are the assigned approver for this request. Please review and take action.
                    </Alert>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<Check />}
                        onClick={() => handleAction(RequestStatus.APPROVED)}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<Close />}
                        onClick={() => handleAction(RequestStatus.REJECTED)}
                      >
                        Reject
                      </Button>
                      {request?.status !== RequestStatus.HOLD && (
                        <Button
                          variant="contained"
                          color="warning"
                          startIcon={<Pause />}
                          onClick={() => handleAction(RequestStatus.HOLD)}
                        >
                          Hold
                        </Button>
                      )}
                      {request?.status === RequestStatus.HOLD && (
                        <Button
                          variant="contained"
                          color="info"
                          startIcon={<Check />}
                          onClick={() => handleAction(RequestStatus.PENDING)}
                        >
                          Resume
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </Paper>
            </motion.div>
          </Grid>

          {/* Comments/Notes Section */}
          <Grid item xs={12} md={4}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  <Comment sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Notes & Comments
                </Typography>
                
                <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                  {/* Show initial submission notes/comments from form */}

                  {/* Leave form notes */}
                  {request.type === 'leave' && request.notes && typeof request.notes === 'string' && request.notes.trim() && (
                        <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1976d2' }}>
                              <Person fontSize="small" />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2">
                                {request.submitterName}
                                <Chip label="Submitter" size="small" sx={{ ml: 1 }} />
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(request.submittedDate), 'dd/MM/yyyy HH:mm')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
                            {request.notes}
                          </Typography>
                        </ListItem>
                      )}

                      {/* WFH comments */}
                      {request.type === 'wfh' && request.wfhComments && request.wfhComments.trim() && (
                        <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1976d2' }}>
                              <Person fontSize="small" />
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle2">
                                {request.submitterName}
                                <Chip label="Submitter" size="small" sx={{ ml: 1 }} />
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {format(new Date(request.submittedDate), 'dd/MM/yyyy HH:mm')}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
                            {request.wfhComments}
                          </Typography>
                        </ListItem>
                      )}

                  {/* Generic comments field */}
                  {request.comments && typeof request.comments === 'string' && request.comments.trim() && (
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: '#1976d2' }}>
                          <Person fontSize="small" />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle2">
                            {request.submitterName}
                            <Chip label="Submitter" size="small" sx={{ ml: 1 }} />
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(request.submittedDate), 'dd/MM/yyyy HH:mm')}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
                        {request.comments}
                      </Typography>
                    </ListItem>
                  )}

                  {/* Approver notes and comments thread from request_comments table */}
                  {request.comments && Array.isArray(request.comments) && request.comments.length > 0 && (
                    request.comments.map((comment) => (
                      <ListItem key={comment.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: comment.isApprover ? '#0078d4' : '#ff6b00' }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2">
                              {comment.author}
                              {comment.isApprover && (
                                <Chip label="Approver" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(comment.timestamp), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
                          {comment.content}
                        </Typography>
                      </ListItem>
                    ))
                  )}

                  {/* Also show old-style notes array for backwards compatibility */}
                  {request.notes && Array.isArray(request.notes) && request.notes.length > 0 && (
                    request.notes.map((note) => (
                      <ListItem key={note.id} sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
                          <Avatar sx={{ width: 32, height: 32, mr: 1, bgcolor: note.isApprover ? '#0078d4' : '#ff6b00' }}>
                            <Person fontSize="small" />
                          </Avatar>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2">
                              {note.author}
                              {note.isApprover && (
                                <Chip label="Approver" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {format(new Date(note.timestamp), 'dd/MM/yyyy HH:mm')}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ pl: 5, whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </Typography>
                      </ListItem>
                    ))
                  )}

                  {!request.notes && !request.leaveReason && !request.wfhComments &&
                   (!request.comments || (Array.isArray(request.comments) && request.comments.length === 0)) && (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No comments yet
                    </Typography>
                  )}
                </List>

                <Divider sx={{ mb: 2 }} />

                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={submittingComment ? <CircularProgress size={20} /> : <Send />}
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                  >
                    {submittingComment ? 'Posting...' : 'Post Comment'}
                  </Button>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedAction === RequestStatus.APPROVED && 'Approve Request'}
          {selectedAction === RequestStatus.REJECTED && 'Reject Request'}
          {selectedAction === RequestStatus.HOLD && 'Put Request on Hold'}
          {selectedAction === RequestStatus.PENDING && 'Resume Request'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Notes (Optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            sx={{ mt: 2 }}
            placeholder="Add any notes or comments..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={processAction}
            disabled={processing}
            startIcon={processing && <CircularProgress size={20} />}
          >
            {processing ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 2,
          px: 2,
          mt: 'auto',
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default RequestDetail;